import { Intent } from "@blueprintjs/core";
import axios from "axios";
import classNames from "classnames";
import { Button } from "components/buttons";
import { DataDisplayRecord } from "components/data-display";
import { DateToLocal } from "components/dates";
import { GridLayout } from "components/grid";
import { ShowHide } from "components/layout";
import { Tabs } from "components/navigation";
import { Modal, ModalSize, NotificationInline, NotificationToaster } from "components/notifications";
import PropTypes from "prop-types";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { GreyFleetService } from 'services';
import { GreyFleetInsuranceAIResults } from "./GreyFleetInsuranceAIResults";
import { GreyFleetInsuranceCoverRecords, getCoverRecordsColumnCount } from "./GreyFleetInsuranceCoverRecords";
import { GreyFleetInsuranceDetails } from "./GreyFleetInsuranceDetails";
import { GreyFleetInsuranceDocument } from "./GreyFleetInsuranceDocument";
import { GreyFleetInsuranceInvalidationModal } from "./GreyFleetInsuranceInvalidationModal";
import "./GreyFleetInsuranceModal.css";
import { GreyFleetInsuranceValidationModal } from "./GreyFleetInsuranceValidationModal";

export function GreyFleetInsuranceModal({
    insurance = null,
    nextInsuranceId = null,
    isOpen = false,
    onClose = () => {},
    onNext = () => {}
}) {
    const notProvidedText = "Not provided";

    const [loading, setLoading] = useState(true);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [tabs, setTabs] = useState([
        { key: "Details", title: "Details", selected: true },
        { key: "Documents", title: "Documents" },
    ]);
    const [currentTab, setCurrentTab] = useState(0);
    const [showValidationModal, setShowValidationModal] = useState(false);
    const [showInvalidationModal, setShowInvalidationModal] = useState(false);
    const [insuranceDetails, setInsuranceDetails] = useState(null);
    const [coverTypes, setCoverTypes] = useState([]);

    const hasApprover = useMemo(() => {
        return insuranceDetails?.human.approver !== null && insuranceDetails?.human.approver !== "";
    }, [insuranceDetails]);

    const hasNextInsurance = useMemo(() => {
        return !!nextInsuranceId;
    }, [nextInsuranceId]);

    const showNextButton = useMemo(() => {
        return hasApprover && hasNextInsurance;
    }, [hasApprover, hasNextInsurance]);

    const numberOfColumns = useMemo(() => {
        return getCoverRecordsColumnCount(insuranceDetails?.userInput, coverTypes);
    }, [insuranceDetails, coverTypes]);

    useEffect(() => {
        if (isOpen && insurance != null) {
            setLoading(true);
            changeCurrentTab(0);
            axios
                .all([
                    GreyFleetService.getInsuranceForReview(insurance.insuranceDocumentId),
                    GreyFleetService.getInsuranceCoverTypes()
                ])
                .then(
                    axios.spread(function (response, coverTypeOptions) {
                        setInsuranceDetails(response || null);
                        setCoverTypes(coverTypeOptions ?? []);
                    }),
                    () => {
                        NotificationToaster.show(Intent.DANGER, "Could not load insurance details. Please refresh the page.");
                        onClose();
                    }
                )
                .catch(() => {
                    NotificationToaster.show(Intent.DANGER, "Could not load insurance details. Please refresh the page.");
                    onClose();
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [isOpen, insurance]);

    function onTabClick(index) {
        if (loading || actionInProgress) {
            return;
        }

        changeCurrentTab(index);
    }
    
    function changeCurrentTab(index) {
        if (index === currentTab) {
            return;
        }

        let clonedTabs = tabs.map(function (t, i) {
            return {
                ...t,
                selected: index === i
            }
        });

        setTabs(clonedTabs);
        setCurrentTab(index);
    }

    function onNextDocument() {
        if (loading) {
            return;
        }

        if (hasNextInsurance) {
            onNext();
        } else {
            onClose();
        }
    }

    return (
        <Fragment>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={"Insurance Validation"}
                size={ModalSize.LARGE}
                clickOutsideClose={!actionInProgress}
                showCloseButton={!actionInProgress}
                className="grey-fleet-insurance-modal"
                footer={
                    <ShowHide
                        evaluator={!hasApprover || hasNextInsurance}
                        show={
                            <ShowHide
                                evaluator={showNextButton}
                                show={
                                    <div className="inline-items push-right">
                                        <Button disabled={loading} text="Next Document" intent={Intent.PRIMARY} onClick={onNextDocument} />
                                    </div>
                                }
                                hide={
                                    <div className="inline-items inline-items-space">
                                        <Button disabled={loading || actionInProgress} text="Invalidate Insurance" intent={Intent.DANGER} onClick={() => {setShowInvalidationModal(true)}} />
                                        <Button disabled={loading || actionInProgress} text="Validate Insurance" intent={Intent.SUCCESS} onClick={() => {setShowValidationModal(true)}} />
                                    </div>
                                }
                            />
                        }
                    />
                }
            >
                <div className="modal-content-flex">
                    <ShowHide
                        evaluator={!loading && hasApprover}
                        show={
                            <div className="spacer-bottom">
                                <NotificationInline
                                    intent={Intent.NONE}
                                    show={!loading && hasApprover}
                                    text={<><strong>Validation Completed: </strong>{"The validation for this document has already been completed."}</>}
                                    allowClose={false}
                                    isBanner={true}
                                />
                            </div>
                        }
                    />

                    <div className="spacer-bottom">
                        <Tabs tabs={tabs} onClick={onTabClick} />
                    </div>

                    <ShowHide
                        evaluator={currentTab === 0}
                        show={
                            <Fragment>
                                <GreyFleetInsuranceDetails
                                    loading={loading}
                                    insuranceDetails={insuranceDetails}
                                    coverTypes={coverTypes}
                                />
                                <GreyFleetInsuranceAIResults
                                    loading={loading}
                                    insuranceDetails={insuranceDetails}
                                />
                            </Fragment>
                        }
                        hide={
                            <Fragment>
                                <div className="spacer-bottom">
                                    <h2>Documents</h2>
                                    <GridLayout gap={40} numberOfColumns={numberOfColumns} className={classNames({ "bp6-skeleton": loading })}>
                                        <DataDisplayRecord header="Driver Name" data={insuranceDetails?.employeeName} />
                                        <DataDisplayRecord header="Vehicle Reg" data={insuranceDetails?.registration} />
                                        <DataDisplayRecord header="Policy Start Date" data={insuranceDetails?.userInput?.startDate ? <DateToLocal key={`policystartdate-${insuranceDetails?.checkId}`} format='DD/MM/YYYY'>{insuranceDetails?.userInput?.startDate}</DateToLocal> : notProvidedText} />
                                        <DataDisplayRecord header="Policy End Date" data={insuranceDetails?.userInput?.endDate ? <DateToLocal key={`policyenddate-${insuranceDetails?.checkId}`} format='DD/MM/YYYY'>{insuranceDetails?.userInput?.endDate}</DateToLocal> : notProvidedText} />
                                        <GreyFleetInsuranceCoverRecords
                                            loading={loading}
                                            userInput={insuranceDetails?.userInput}
                                            coverTypes={coverTypes}
                                        />
                                    </GridLayout>
                                </div>
                                <GreyFleetInsuranceDocument
                                    url={insuranceDetails?.document?.url}
                                    iframeHeight="100%"
                                />
                            </Fragment>
                        }
                    />
                    
                </div>
            </Modal>
            <GreyFleetInsuranceValidationModal
                isOpen={showValidationModal}
                onClose={() => setShowValidationModal(false)}
                onSuccess={() => {setShowValidationModal(false); onClose();}}
                onNext={() => {setShowValidationModal(false); onNextDocument();}}
                insuranceDetails={insuranceDetails}
                actionInProgress={actionInProgress}
                setActionInProgress={setActionInProgress}
                nextInsuranceId={nextInsuranceId}
            />
            <GreyFleetInsuranceInvalidationModal
                isOpen={showInvalidationModal}
                onClose={() => setShowInvalidationModal(false)}
                onSuccess={() => {setShowInvalidationModal(false); onClose();}}
                onNext={() => {setShowInvalidationModal(false); onNextDocument();}}
                insuranceDetails={insuranceDetails}
                actionInProgress={actionInProgress}
                setActionInProgress={setActionInProgress}
                nextInsuranceId={nextInsuranceId}
            />
        </Fragment>
    );
}

GreyFleetInsuranceModal.propTypes = {
    insurance: PropTypes.object,
    nextInsuranceId: PropTypes.number,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onNext: PropTypes.func
};